import {
    MediaType,
    MIME_TYPE_MAPPINGS,
    MAX_FILE_SIZES,
    FILE_EXTENSIONS,
} from "../constants/mediaConstants";

/**
 * Determines the media type based on file MIME type or extension
 */
export const getMediaType = (file: File): MediaType | null => {
    const mimeType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    // Check by MIME type first
    for (const mediaType of Object.keys(MIME_TYPE_MAPPINGS) as MediaType[]) {
        const mimeTypes = MIME_TYPE_MAPPINGS[mediaType] as readonly string[];
        if ((mimeTypes as string[]).includes(mimeType)) {
            return mediaType;
        }
    }

    // Fallback to file extension
    for (const mediaType of Object.keys(FILE_EXTENSIONS) as MediaType[]) {
        const extensions = FILE_EXTENSIONS[mediaType];
        if (extensions.some((ext) => fileName.endsWith(ext))) {
            return mediaType;
        }
    }

    return null;
};

/**
 * Validates file size against media type limits
 */
export const validateFileSize = (file: File, mediaType: MediaType): boolean => {
    const maxSize = MAX_FILE_SIZES[mediaType];
    return file.size <= maxSize;
};

/**
 * Validates file type against allowed MIME types
 */
export const validateFileType = (file: File, mediaType: MediaType): boolean => {
    const allowedMimeTypes = MIME_TYPE_MAPPINGS[mediaType] as readonly string[];
    return (allowedMimeTypes as string[]).includes(file.type.toLowerCase());
};

/**
 * Formats file size into human-readable format
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Gets the appropriate icon name for a media type
 */
export const getMediaIcon = (mediaType: MediaType): string => {
    const iconMap = {
        image: "PhotoOutlined",
        video: "VideoCallOutlined",
        pdf: "PictureAsPdfOutlined",
        gif: "GifOutlined",
        document: "AttachFileOutlined",
    };

    return iconMap[mediaType] || "AttachFileOutlined";
};

/**
 * Gets the display name for a media type
 */
export const getMediaDisplayName = (mediaType: MediaType): string => {
    const displayNames = {
        image: "Photo",
        video: "Video",
        pdf: "PDF",
        gif: "GIF",
        document: "Document",
    };

    return displayNames[mediaType] || "File";
};

/**
 * Checks if file is an image type
 */
export const isImageFile = (file: File): boolean => {
    return getMediaType(file) === "image";
};

/**
 * Checks if file is a video type
 */
export const isVideoFile = (file: File): boolean => {
    return getMediaType(file) === "video";
};

/**
 * Checks if file is a PDF
 */
export const isPDFFile = (file: File): boolean => {
    return getMediaType(file) === "pdf";
};

/**
 * Checks if file is a GIF
 */
export const isGIFFile = (file: File): boolean => {
    return getMediaType(file) === "gif";
};

/**
 * Checks if file is a document
 */
export const isDocumentFile = (file: File): boolean => {
    return getMediaType(file) === "document";
};

/**
 * Generates a preview URL for different media types
 */
export const generatePreviewUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            resolve(e.target?.result as string);
        };

        reader.onerror = () => {
            reject(new Error("Failed to read file"));
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Validates multiple files against media upload limits
 */
export const validateMultipleFiles = (
    files: File[],
    currentMediaCount: number = 0,
): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const mediaTypeCounts: Record<MediaType, number> = {
        image: 0,
        video: 0,
        pdf: 0,
        gif: 0,
        document: 0,
    };

    // Count existing media
    const totalCount = currentMediaCount + files.length;
    if (totalCount > 10) {
        errors.push(
            `Cannot upload more than 10 media items per post. You have ${currentMediaCount} and are trying to add ${files.length} more.`,
        );
    }

    // Validate each file
    for (const file of files) {
        const mediaType = getMediaType(file);

        if (!mediaType) {
            errors.push(`File "${file.name}" has an unsupported format.`);
            continue;
        }

        if (!validateFileSize(file, mediaType)) {
            const maxSize = formatFileSize(MAX_FILE_SIZES[mediaType]);
            errors.push(`File "${file.name}" exceeds the ${maxSize} limit for ${mediaType} files.`);
        }

        if (!validateFileType(file, mediaType)) {
            errors.push(`File "${file.name}" has an invalid file type for ${mediaType}.`);
        }

        mediaTypeCounts[mediaType]++;
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};

/**
 * Converts file to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result as string);
        };

        reader.onerror = () => {
            reject(new Error("Failed to convert file to base64"));
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
};
